"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Badge } from "@/components/ui/badge";
const steps = [{
  num: "01",
  title: "Registration",
  desc: "Submit your supplier profile through our secure portal. We verify credentials to ensure quality and compliance for our global trading network.",
  fragment: "capture"
}, {
  num: "02",
  title: "Integration",
  desc: "Connect your inventory systems to our platform. Real-time data synchronization ensures your stock levels are always visible to verified buyers.",
  fragment: "visualize"
}, {
  num: "03",
  title: "Automation",
  desc: "Set automated pricing and fulfillment rules. Our system handles order routing and documentation, reducing manual administrative overhead.",
  fragment: "automate"
}, {
  num: "04",
  title: "Optimization",
  desc: "Access detailed performance analytics. Identify high-demand product categories and optimize your supply chain for maximum profitability.",
  fragment: "measure"
}];
function CaptureFragment() {
  return <div className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-secondary)] p-4 md:p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <div className="h-2 w-2 rounded-lg bg-[var(--color-primary)]" />
        <span className="text-[11px] font-sans uppercase tracking-[0.14em] text-[var(--color-primary)]">
          Profile Setup
        </span>
      </div>
      <div className="flex items-center gap-2 rounded-lg border border-[var(--color-border)] bg-slate-50 px-3 py-2.5">
        <span className="text-sm text-[var(--color-text-dark)]">Verify business credentials</span>
        <span className="inline-block h-4 w-px bg-[var(--color-primary)] animate-pulse" aria-hidden />
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        <Badge className="bg-slate-100 text-[var(--color-text-dark)] border-transparent rounded-lg text-[10px] tracking-[0.12em] uppercase">
          Verified
        </Badge>
        <Badge className="bg-[var(--color-primary)]/10 text-[var(--color-primary)] border-transparent rounded-lg text-[10px] tracking-[0.12em] uppercase">
          Active
        </Badge>
        <Badge className="bg-slate-100 text-[var(--color-text-dark)] border-transparent rounded-lg text-[10px] tracking-[0.12em] uppercase">
          Global
        </Badge>
      </div>
    </div>;
}
function VisualizeFragment() {
  const columns = [{
    name: "Pending",
    count: 6,
    items: 3
  }, {
    name: "Verified",
    count: 4,
    items: 2,
    highlight: true
  }, {
    name: "Active",
    count: 9,
    items: 3
  }];
  return <div className="grid grid-cols-3 gap-2">
      {columns.map((col, i) => <div key={i} data-index={i} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-secondary)] p-3 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] uppercase tracking-[0.14em] text-[var(--color-text-dark)] font-semibold">
              {col.name}
            </span>
            <span className="text-[10px] font-mono text-[var(--color-text-dark)]">{col.count}</span>
          </div>
          <div className="space-y-1.5">
            {Array.from({
          length: col.items
        }).map((_, j) => <div key={j} className={`h-7 rounded-md border ${col.highlight && j === 0 ? "border-[var(--color-primary)]/40 bg-[var(--color-primary)]/10" : "border-[var(--color-border)] bg-slate-50"}`} />)}
          </div>
        </div>)}
    </div>;
}
function AutomateFragment() {
  const pills = ["WHEN order = New", "THEN notify = Supplier", "AND generate = Invoice"];
  return <div className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-secondary)] p-4 md:p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <div className="h-2 w-2 rounded-lg bg-[var(--color-accent)] animate-pulse" />
        <span className="text-[11px] font-sans uppercase tracking-[0.14em] text-[var(--color-text-dark)]">
          Workflow Active
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {pills.map((p, i) => <div key={i} data-index={i} className="flex items-center gap-2">
            <span className="inline-flex items-center rounded-lg border border-[var(--color-accent)]/30 bg-[var(--color-accent)]/10 px-2.5 py-1.5 text-[11px] font-mono text-[var(--color-primary)]">
              {p}
            </span>
            {i < pills.length - 1 && <span className="text-[var(--color-text-dark)] text-xs">{"→"}</span>}
          </div>)}
      </div>
    </div>;
}
function MeasureFragment() {
  const points = [{
    x: 4,
    y: 38
  }, {
    x: 18,
    y: 30
  }, {
    x: 32,
    y: 34
  }, {
    x: 46,
    y: 22
  }, {
    x: 60,
    y: 26
  }, {
    x: 74,
    y: 14
  }, {
    x: 88,
    y: 18
  }, {
    x: 96,
    y: 10
  }];
  const path = points.map((p, i) => i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`).join(" ");
  const last = points[points.length - 1];
  return <div className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-secondary)] p-4 md:p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--color-text-dark)] font-semibold">
            Growth Rate
          </div>
          <div className="text-xl font-sans font-bold text-[var(--color-primary)] mt-0.5">
            {"+42%"}
          </div>
        </div>
        <Badge className="bg-[var(--color-accent)]/20 text-[var(--color-primary)] border-transparent rounded-lg text-[10px] tracking-[0.12em] uppercase">
          Scaling
        </Badge>
      </div>
      <svg viewBox="0 0 100 50" className="w-full h-16" preserveAspectRatio="none">
        <path d={path} fill="none" stroke="var(--color-primary)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={last.x} cy={last.y} r="2.4" fill="var(--color-accent)" />
        <circle cx={last.x} cy={last.y} r="4.5" fill="var(--color-accent)" fillOpacity="0.2" />
      </svg>
    </div>;
}
function FragmentRenderer({
  kind
}: {
  kind: string;
}) {
  if (kind === "capture") return <CaptureFragment />;
  if (kind === "visualize") return <VisualizeFragment />;
  if (kind === "automate") return <AutomateFragment />;
  if (kind === "measure") return <MeasureFragment />;
  return null;
}
function StepTile({
  num,
  title,
  desc,
  fragment,
  i
}: {
  num: string;
  title: string;
  desc: string;
  fragment: string;
  i: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <div ref={ref} data-index={i} className={`group rounded-lg border border-[var(--color-border)] bg-[var(--color-secondary)] p-8 md:p-10 transition-all duration-[520ms] ease-out hover:-translate-y-1 hover:shadow-md ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
    transitionDelay: `${i * 80}ms`
  }}>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
        <div className="lg:col-span-5">
          <div className="text-4xl md:text-5xl font-sans font-bold text-[var(--color-border)] tracking-tight group-hover:text-[var(--color-primary)]/20 transition-colors duration-300">
            {num}
          </div>
          <h3 className="mt-4 text-2xl md:text-3xl font-sans font-semibold tracking-tight text-[var(--color-text-dark)]">
            {title}
          </h3>
          <p className="mt-3 text-base text-[var(--color-text-dark)]/80 leading-relaxed max-w-md">
            {desc}
          </p>
        </div>
        <div className="lg:col-span-7">
          <FragmentRenderer kind={fragment} />
        </div>
      </div>
    </div>;
}
export default function Supplierpartnershipandfaq() {
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="16609" id="supplierpartnershipandfaq" className="bg-[var(--color-secondary)] text-[var(--color-text-dark)] py-24 md:py-32 px-5 md:px-10">
      <div className="max-w-7xl mx-auto">
        <div ref={introRef} className={`max-w-2xl mx-auto text-center mb-14 transition-all duration-[520ms] ease-out ${introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] bg-slate-50 px-3 py-1.5 mb-6">
            <span className="h-1.5 w-1.5 rounded-lg bg-[var(--color-accent)]" />
            <span className="text-[11px] uppercase tracking-[0.14em] text-[var(--color-text-dark)] font-semibold">
              Partnership Process
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-sans font-bold tracking-tight leading-[1.04] text-[var(--color-text-dark)]">
            Building a{" "}
            <span className="italic text-[var(--color-primary)]">seamless</span> supply chain
          </h2>
          <p className="mt-5 text-lg text-[var(--color-text-dark)]/80 leading-relaxed">
            Our streamlined onboarding and integration process ensures your business is ready to trade globally with minimal friction and maximum efficiency.
          </p>
        </div>

        <div className="flex flex-col gap-5">
          {steps.map((step, i) => <StepTile key={step.num} i={i} num={step.num} title={step.title} desc={step.desc} fragment={step.fragment} />)}
        </div>
      </div>
    </section>;
}